/**
 * Pure visibility and grouping resolver. Returns winner marker ids only.
 *
 * This function determines which markers should be visible after:
 * - Applying default visibility rules
 * - Optionally applying external visibility overrides
 * - Grouping markers by computed group keys
 * - Selecting the highest zIndex marker per group
 *
 * ---
 *
 * @param {Object} params
 * @param {Array<Object>} params.markers
 * Array of marker records. Each record must contain:
 * - markerData: { id: string, position: any, zIndex: number }
 *
 * @param {Object} params.bounds
 * Current map bounds used for visibility evaluation.
 *
 * @param {number} params.zoom
 * Current map zoom level used for grouping logic.
 *
 * @param {Object} [params.behavior]
 * Optional behavior overrides. Must remain PURE (no side effects).
 *
 * @param {boolean} [params.behavior.isInteracting=false]
 * Flag indicating whether the user is currently interacting with the map.
 *
 * @param {Function} [params.behavior.resolveMarkerVisibility]
 * Optional visibility override function.
 *
 * Signature:
 *   (markerData: Object, context: {
 *     zoom: number,
 *     isInteracting: boolean,
 *     bounds: Object,
 *     defaultVisibility: boolean
 *   }) => boolean | undefined
 *
 * Behavior:
 * - If returns true → marker is visible
 * - If returns false → marker is hidden
 * - If returns undefined → fallback to default visibility logic
 *
 * Purity contract:
 * - MUST be a pure function
 * - MUST NOT read or mutate external state (no closures over mutable globals)
 * - MUST NOT cause side effects (DOM, network, store writes)
 *
 * @param {Function} [params.behavior.computeDefaultVisibility]
 * Base visibility function used when no override applies.
 * Must be pure. Defaults to always-visible behavior.
 *
 * Signature:
 *   (record: Object, bounds: Object) => boolean
 *
 * @param {Function} [params.behavior.resolveGroupingKey]
 * Function used to compute grouping key for clustering logic.
 *
 * Signature:
 *   (markerData: Object, context: { zoom: number }) => string | number
 *
 * @returns {ReadonlySet<string>}
 * Set of marker IDs that are considered visible after grouping resolution.
 */
import { getGroupKey, isWithinBounds } from './utils';

/**
 * Default visibility predicate: a marker is visible if its position falls within the current bounds.
 */
function computeDefaultVisibility(record, bounds) {
  return isWithinBounds(record.markerData.position, bounds);
}

export function resolveDefaultGroupingKey(markerData, { zoom }) {
  return getGroupKey(markerData.position, zoom);
}

/**
 * Pure visibility/grouping resolver. Returns winner marker ids only.
 */
export function computeVisibleMarkers({
  markers,
  bounds,
  zoom,
  behavior = {},
}) {
  const {
    isInteracting = false,
    resolveMarkerVisibility,
    resolveDefaultVisibility = computeDefaultVisibility,
    resolveGroupingKey = resolveDefaultGroupingKey,
  } = behavior;

  const winnersByGroup = new Map();
  markers.forEach((record) => {
    if (!record?.markerData) {
      return;
    }

    const markerData = record.markerData;
    const defaultVisible = resolveDefaultVisibility(record, bounds);

    if (!defaultVisible) {
      return;
    }

    const resolvedVisibility = resolveMarkerVisibility?.(markerData, {
      zoom,
      isInteracting,
      bounds,
      defaultVisibility: true,
    });

    const hasCustomVisibility = typeof resolvedVisibility === 'boolean';

    /**
     * Default assumption:
     * Markers are visible unless explicitly hidden by a visibility resolver.
     */
    const visible = hasCustomVisibility ? resolvedVisibility : true;

    if (!visible) {
      return;
    }

    const groupKey = resolveGroupingKey(markerData, {
      zoom,
    });

    const currentTop = winnersByGroup.get(groupKey);
    if (!currentTop || markerData.zIndex > currentTop.markerData.zIndex) {
      winnersByGroup.set(groupKey, record);
    }
  });

  const visibleIds = new Set();
  winnersByGroup.forEach((record) => visibleIds.add(record.markerData.id));

  return visibleIds;
}
